import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import {
    Alert,
    Button,
    Chip,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Skeleton,
    Stack,
    Table,
    TableBody,
    TableContainer,
    TableHead,
    TextField,
    Typography,
} from "@mui/material";
import Popup from "../../../components/Popup";
import { StyledTableCell, StyledTableRow } from "../../../components/styles";
import { buildApiUrl } from "../../../config/api";
import {
    DISCIPLINARY_OUTCOMES,
    DISCIPLINARY_STATUS_CHIP,
} from "../../../config/disciplinaryConstants";

const formatDate = (d) => {
    if (!d) return "—";
    const x = new Date(d);
    return Number.isNaN(x.getTime()) ? "—" : x.toLocaleDateString("en-GB");
};

const STATUS_OPTIONS = ["All", "Pending", "Verified", "Rejected"];

const DisciplinaryApprovals = () => {
    const { currentUser } = useSelector((s) => s.user);
    const schoolId = currentUser?._id;

    const [loading, setLoading] = useState(true);
    const [rows, setRows] = useState([]);
    const [statusFilter, setStatusFilter] = useState("Pending");

    const [showPopup, setShowPopup] = useState(false);
    const [message, setMessage] = useState("");
    const [actionLoading, setActionLoading] = useState(false);

    const [verifyOpen, setVerifyOpen] = useState(false);
    const [verifyTargetId, setVerifyTargetId] = useState(null);
    const [outcome, setOutcome] = useState("Strong Warning");
    const [suspensionDays, setSuspensionDays] = useState("3");
    const [directorNotes, setDirectorNotes] = useState("");

    const [rejectOpen, setRejectOpen] = useState(false);
    const [rejectTargetId, setRejectTargetId] = useState(null);
    const [rejectionNote, setRejectionNote] = useState("");

    const loadCases = useCallback(async () => {
        if (!schoolId) return;
        setLoading(true);
        try {
            const params = statusFilter !== "All" ? `?status=${encodeURIComponent(statusFilter)}` : "";
            const { data } = await axios.get(
                buildApiUrl(`/DisciplinaryCases/school/${schoolId}${params}`)
            );
            setRows(Array.isArray(data) ? data : []);
        } catch (err) {
            setMessage(err.response?.data?.message || "Could not load disciplinary cases");
            setShowPopup(true);
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, [schoolId, statusFilter]);

    useEffect(() => {
        loadCases();
    }, [loadCases]);

    const summary = useMemo(() => {
        const pending = rows.filter((r) => r.status === "Pending").length;
        const verified = rows.filter((r) => r.status === "Verified").length;
        return { total: rows.length, pending, verified };
    }, [rows]);

    const openVerify = (id) => {
        setVerifyTargetId(id);
        setOutcome("Strong Warning");
        setSuspensionDays("3");
        setDirectorNotes("");
        setVerifyOpen(true);
    };

    const handleVerify = async () => {
        if (!verifyTargetId) return;
        setActionLoading(true);
        try {
            const payload = {
                verifiedByAdminId: schoolId,
                outcome,
                directorNotes: directorNotes.trim(),
            };
            if (outcome === "Suspension") {
                payload.suspensionDays = Number(suspensionDays) || 0;
            }
            const { data } = await axios.put(
                buildApiUrl(`/DisciplinaryCase/${verifyTargetId}/verify`),
                payload
            );
            if (data?.message && !data?.id && !data?._id) {
                setMessage(data.message);
                setShowPopup(true);
                return;
            }
            setMessage("Case verified and outcome recorded");
            setShowPopup(true);
            setVerifyOpen(false);
            setVerifyTargetId(null);
            await loadCases();
        } catch (err) {
            setMessage(err.response?.data?.message || "Could not verify case");
            setShowPopup(true);
        } finally {
            setActionLoading(false);
        }
    };

    const openReject = (id) => {
        setRejectTargetId(id);
        setRejectionNote("");
        setRejectOpen(true);
    };

    const handleReject = async () => {
        if (!rejectTargetId) return;
        setActionLoading(true);
        try {
            const { data } = await axios.put(
                buildApiUrl(`/DisciplinaryCase/${rejectTargetId}/reject`),
                {
                    verifiedByAdminId: schoolId,
                    rejectionNote: rejectionNote.trim(),
                }
            );
            if (data?.message && !data?.id && !data?._id) {
                setMessage(data.message);
                setShowPopup(true);
                return;
            }
            setMessage("Case returned to form teacher");
            setShowPopup(true);
            setRejectOpen(false);
            setRejectTargetId(null);
            await loadCases();
        } catch (err) {
            setMessage(err.response?.data?.message || "Could not reject case");
            setShowPopup(true);
        } finally {
            setActionLoading(false);
        }
    };

    return (
        <Container maxWidth="lg" sx={{ py: { xs: 2, md: 3 } }}>
            <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5 }}>
                Disciplinary cases
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Form teachers report offenses; you verify and assign strong warning, suspension, or
                expulsion.
            </Typography>

            <Stack direction={{ xs: "column", sm: "row" }} justifyContent="flex-end" sx={{ mb: 2 }}>
                <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel>Status</InputLabel>
                    <Select label="Status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                        {STATUS_OPTIONS.map((s) => (
                            <MenuItem key={s} value={s}>
                                {s}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Stack>

            <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2 }}>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                        <Typography variant="caption" color="text.secondary">
                            Shown
                        </Typography>
                        <Typography variant="h6" fontWeight={700}>
                            {summary.total}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Typography variant="caption" color="text.secondary">
                            Pending
                        </Typography>
                        <Typography variant="h6" fontWeight={700} color="warning.main">
                            {summary.pending}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Typography variant="caption" color="text.secondary">
                            Verified
                        </Typography>
                        <Typography variant="h6" fontWeight={700} color="success.main">
                            {summary.verified}
                        </Typography>
                    </Grid>
                </Grid>
            </Paper>

            <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
                <TableContainer sx={{ overflowX: "auto" }}>
                    <Table size="small">
                        <TableHead>
                            <StyledTableRow>
                                <StyledTableCell>Date</StyledTableCell>
                                <StyledTableCell>Student</StyledTableCell>
                                <StyledTableCell>Class</StyledTableCell>
                                <StyledTableCell>Offense</StyledTableCell>
                                <StyledTableCell>Reported by</StyledTableCell>
                                <StyledTableCell>Details</StyledTableCell>
                                <StyledTableCell>Status</StyledTableCell>
                                <StyledTableCell>Outcome</StyledTableCell>
                                <StyledTableCell align="right">Actions</StyledTableCell>
                            </StyledTableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <StyledTableRow>
                                    <StyledTableCell colSpan={9}>
                                        <Skeleton height={48} />
                                    </StyledTableCell>
                                </StyledTableRow>
                            ) : rows.length === 0 ? (
                                <StyledTableRow>
                                    <StyledTableCell colSpan={9} align="center" sx={{ py: 4 }}>
                                        <Typography variant="body2" color="text.secondary">
                                            No disciplinary cases found.
                                        </Typography>
                                    </StyledTableCell>
                                </StyledTableRow>
                            ) : (
                                rows.map((row) => {
                                    const id = row.id || row._id;
                                    const status = row.status || "Pending";
                                    return (
                                        <StyledTableRow key={id} hover>
                                            <StyledTableCell>{formatDate(row.incidentDate)}</StyledTableCell>
                                            <StyledTableCell>
                                                {row.student?.name || "—"}
                                            </StyledTableCell>
                                            <StyledTableCell>
                                                {row.class?.sclassName || "—"}
                                            </StyledTableCell>
                                            <StyledTableCell>{row.offenseCategory}</StyledTableCell>
                                            <StyledTableCell>
                                                {row.reportedByTeacher?.name || "—"}
                                            </StyledTableCell>
                                            <StyledTableCell sx={{ maxWidth: 200 }}>
                                                <Typography variant="body2" noWrap title={row.incidentDescription}>
                                                    {row.incidentDescription || "—"}
                                                </Typography>
                                                {row.recommendedOutcome ? (
                                                    <Typography variant="caption" color="text.secondary">
                                                        Suggested: {row.recommendedOutcome}
                                                    </Typography>
                                                ) : null}
                                            </StyledTableCell>
                                            <StyledTableCell>
                                                <Chip
                                                    label={status}
                                                    size="small"
                                                    color={DISCIPLINARY_STATUS_CHIP[status] || "default"}
                                                />
                                            </StyledTableCell>
                                            <StyledTableCell>
                                                {row.outcome || "—"}
                                                {row.suspensionDays ? (
                                                    <Typography variant="caption" display="block">
                                                        {row.suspensionDays} day(s)
                                                    </Typography>
                                                ) : null}
                                            </StyledTableCell>
                                            <StyledTableCell align="right">
                                                {status === "Pending" ? (
                                                    <Stack
                                                        direction="row"
                                                        spacing={0.5}
                                                        justifyContent="flex-end"
                                                        flexWrap="wrap"
                                                    >
                                                        <Button
                                                            size="small"
                                                            variant="contained"
                                                            color="primary"
                                                            disabled={actionLoading}
                                                            onClick={() => openVerify(id)}
                                                        >
                                                            Verify
                                                        </Button>
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            color="error"
                                                            disabled={actionLoading}
                                                            onClick={() => openReject(id)}
                                                        >
                                                            Reject
                                                        </Button>
                                                    </Stack>
                                                ) : null}
                                            </StyledTableCell>
                                        </StyledTableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            <Dialog open={verifyOpen} onClose={() => setVerifyOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Verify disciplinary case</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ pt: 1 }}>
                        <Alert severity="warning" sx={{ borderRadius: 2 }}>
                            Expulsion and suspension update the student&apos;s admission status and
                            disable portal access.
                        </Alert>
                        <FormControl fullWidth required>
                            <InputLabel>Outcome</InputLabel>
                            <Select
                                label="Outcome"
                                value={outcome}
                                onChange={(e) => setOutcome(e.target.value)}
                            >
                                {DISCIPLINARY_OUTCOMES.map((o) => (
                                    <MenuItem key={o} value={o}>
                                        {o}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        {outcome === "Suspension" ? (
                            <TextField
                                fullWidth
                                type="number"
                                label="Suspension days"
                                value={suspensionDays}
                                onChange={(e) => setSuspensionDays(e.target.value)}
                                inputProps={{ min: 1 }}
                                helperText="Counted from the incident date"
                            />
                        ) : null}
                        <TextField
                            fullWidth
                            multiline
                            minRows={3}
                            label="Director notes (optional)"
                            value={directorNotes}
                            onChange={(e) => setDirectorNotes(e.target.value)}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setVerifyOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        color={outcome === "Expulsion" ? "error" : "primary"}
                        onClick={handleVerify}
                        disabled={actionLoading}
                    >
                        Confirm {outcome}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={rejectOpen} onClose={() => setRejectOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Reject / return case</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        multiline
                        minRows={3}
                        label="Reason for rejection"
                        value={rejectionNote}
                        onChange={(e) => setRejectionNote(e.target.value)}
                        sx={{ mt: 1 }}
                        helperText="Form teacher can submit a new case with corrections"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRejectOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleReject}
                        disabled={actionLoading}
                    >
                        Reject
                    </Button>
                </DialogActions>
            </Dialog>

            <Popup message={message} showPopup={showPopup} setShowPopup={setShowPopup} />
        </Container>
    );
};

export default DisciplinaryApprovals;
